import { UnreachableCaseError } from '@lib/validation';
import { PriceStrategy } from '@snap-types/models/enums';
import type { PriceStrategyConfigUpdate } from '@snap-types/models/price-strategy-config';
import { Decimal } from '@snap-types/types/decimal';

export const priceStrategyTypesForSite: Record<string, PriceStrategy[]> = {
  adf: [
    PriceStrategy.FixedCustomer,
    PriceStrategy.FixedDefault,
    PriceStrategy.FixedDiscount,
    PriceStrategy.FixedMarket,
    PriceStrategy.NewPartUserPrice,
    PriceStrategy.TargetContributionMargin,
    PriceStrategy.TargetGrossMargin,
    PriceStrategy.UserPrice,
  ],
  cap: [
    PriceStrategy.CAPPriceSegmentation,
    PriceStrategy.FixedCustomer,
    PriceStrategy.FixedDefault,
    PriceStrategy.FixedDiscount,
    PriceStrategy.FixedMarket,
    PriceStrategy.TargetContributionMargin,
    PriceStrategy.TargetGrossMargin,
    PriceStrategy.UserPrice,
  ],
  raf: [
    PriceStrategy.FixedCustomer,
    PriceStrategy.FixedDefault,
    PriceStrategy.FixedDiscount,
    PriceStrategy.PriceSegmentation,
    PriceStrategy.RAFFixedMarket,
    PriceStrategy.RAFTargetGrossMargin,
    PriceStrategy.TargetContributionMargin,
    PriceStrategy.UserPrice,
  ],
  usa: [
    PriceStrategy.FixedCustomer,
    PriceStrategy.FixedDefault,
    PriceStrategy.FixedDiscount,
    PriceStrategy.FixedMarket,
    PriceStrategy.NewPartUserCost,
    PriceStrategy.NewPartUserPrice,
    PriceStrategy.PriceSegmentation,
    PriceStrategy.TargetContributionMargin,
    PriceStrategy.TargetGrossMargin,
    PriceStrategy.UserPrice,
  ],
  default: [
    PriceStrategy.FixedCustomer,
    PriceStrategy.FixedDefault,
    PriceStrategy.FixedDiscount,
    PriceStrategy.FixedMarket,
    PriceStrategy.NewPartUserPrice,
    PriceStrategy.PriceSegmentation,
    PriceStrategy.TargetContributionMargin,
    PriceStrategy.TargetGrossMargin,
    PriceStrategy.UserPrice,
  ],
};

export const priceStrategyConfigOptionLabels = {
  FixedCustomer: 'Fixed Customer',
  FixedDefault: 'Fixed Default',
  FixedDiscount: 'Fixed Discount',
  FixedMarket: 'Fixed Market',
  RAFFixedMarket: 'Fixed Market',
  UserImport: 'User Import',
  UserPrice: 'User Price',
  NewPartUserPrice: 'New Part User Price',
  NewPartUserCost: 'New Part User Cost',
  TargetGrossMargin: 'Target Gross Margin',
  RAFTargetGrossMargin: 'Target Gross Margin',
  NewPart: 'New Part',
  TargetContributionMargin: 'Target Contribution Margin',
  PriceSegmentation: 'Price Segmentation',
  CAPPriceSegmentation: 'Price Segmentation',
} as const satisfies Record<PriceStrategy, string>;

export function getAvailablePriceStrategies(
  siteCode: string,
  existingTypes: PriceStrategy[] = [],
): { all: PriceStrategy[]; available: PriceStrategy[] } {
  const siteAvailableTypes =
    priceStrategyTypesForSite[siteCode.toLowerCase()] ??
    priceStrategyTypesForSite.default;

  return {
    all: siteAvailableTypes,
    available: siteAvailableTypes.filter(
      (type) => !existingTypes.includes(type),
    ),
  };
}

export function getDefaultPriceStrategyParamsForType(
  type: PriceStrategy,
  {
    discountCodes,
    marketCodes,
  }: { discountCodes: string[]; marketCodes: string[] } = {
    discountCodes: [],
    marketCodes: [],
  },
): Extract<PriceStrategyConfigUpdate, { priceStrategyType: PriceStrategy }> {
  switch (type) {
    case PriceStrategy.TargetGrossMargin:
      return {
        sortOrder: 1,
        enabled: true,
        priceStrategyType: PriceStrategy.TargetGrossMargin,
        targetGrossMargin: new Decimal('0.3'),
      };
    case PriceStrategy.TargetContributionMargin:
      return {
        sortOrder: 1,
        enabled: true,
        priceStrategyType: PriceStrategy.TargetContributionMargin,
        targetContributionMargin: new Decimal('0.2'),
      };
    case PriceStrategy.PriceSegmentation:
      return {
        sortOrder: 1,
        enabled: true,
        priceStrategyType: PriceStrategy.PriceSegmentation,
        priceSegmentConfigs: [],
        quantityRange: new Decimal('10'),
      };
    case PriceStrategy.CAPPriceSegmentation:
      return {
        sortOrder: 1,
        enabled: true,
        priceStrategyType: PriceStrategy.CAPPriceSegmentation,
        priceSegmentConfigs: [],
        targetGrossMargin: new Decimal('30'),
      };
    case PriceStrategy.FixedDiscount:
      return {
        sortOrder: 1,
        enabled: true,
        priceStrategyType: PriceStrategy.FixedDiscount,
        discountCode: discountCodes[0] ?? '',
      };
    case PriceStrategy.FixedMarket:
      return {
        sortOrder: 1,
        enabled: true,
        priceStrategyType: PriceStrategy.FixedMarket,
        marketCode: marketCodes[0] ?? '',
      };
    case PriceStrategy.RAFFixedMarket:
      return {
        sortOrder: 1,
        enabled: true,
        priceStrategyType: PriceStrategy.RAFFixedMarket,
        marketCode: marketCodes[0] ?? '',
      };
    case PriceStrategy.RAFTargetGrossMargin:
      return {
        sortOrder: 1,
        enabled: true,
        priceStrategyType: PriceStrategy.RAFTargetGrossMargin,
        targetGrossMargin: new Decimal('0.3'),
      };
    case PriceStrategy.NewPart:
      return {
        sortOrder: 1,
        enabled: true,
        priceStrategyType: PriceStrategy.NewPart,
        targetGrossMargin: new Decimal('0.3'),
      };
    case PriceStrategy.FixedCustomer:
    case PriceStrategy.FixedDefault:
    case PriceStrategy.UserImport:
    case PriceStrategy.UserPrice:
    case PriceStrategy.NewPartUserPrice:
    case PriceStrategy.NewPartUserCost:
      return {
        sortOrder: 1,
        enabled: true,
        priceStrategyType: type,
      } as PriceStrategyConfigUpdate;
    default:
      throw new UnreachableCaseError(type);
  }
}
